import axiosInstance from "@/api/axios";
import { configToUse } from "@/Keycloak";
import type { Creative, Platform } from "@/screens/creatives/types";
interface CreateLabelResponse {
  id: string;
  title: string;
  prompt: string;
  creative_type: "image" | "video";
  label_type: "unique" | "multiple";
  created_at: string;
  updated_at: string;
  description_configs: Array<{
    id: string;
    title: string;
    prompt: string;
    creative_type: string;
    created_at: string;
    updated_at: string;
    use_count: number;
  }>;
  metadata_configs: string[];
}

export const createLabel = async (
  accountId: string,
  data: {
    title: string;
    prompt: string;
    description_configs?: string[];
    metadata_configs?: string[];
    creative_type: string | null;
    label_type: "unique" | "multiple";
    report_type?: string;
  },
): Promise<CreateLabelResponse> => {
  const res = await axiosInstance.post<CreateLabelResponse>(
    `/creative/${accountId}/label/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export interface LabelGroupCreativePreview extends Creative {
  creative_id: string;
  ad_id?: string;
  ad_name: string;
  type: "image" | "video" | "mixed";
  platform?: Platform;
}

export interface LabelGroup {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  total_creatives?: number;
  creatives_preview?: LabelGroupCreativeItem[];
}

interface CreateLabelV2Response {
  id: string;
  title: string;
  prompt: string;
  creative_type: string | null;
  label_type: "unique" | "multiple";
  sync_status: string;
  sync_progress: string | null;
  version: number;
  label_groups: LabelGroup[];
  label_sources: Record<string, unknown> | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export const createLabelV2 = async (
  accountId: string,
  data: {
    title: string;
    prompt: string;
    creative_type: string | null;
    label_type: "unique" | "multiple";
    label_sources: {
      label_source: "auto" | "creative" | "ad_metadata";
      overview_selection_type?: "auto" | "manual";
      metadata_selection_type?: "auto" | "manual";
      overview_ids: string[];
      metadata: string[];
    };
    report_type?: string;
  },
): Promise<CreateLabelV2Response> => {
  const res = await axiosInstance.post<CreateLabelV2Response>(
    `/creative/${accountId}/label-v2/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface CreateDescriptionResponse {
  id: string;
  title: string;
  prompt: string;
  creative_type: string | null;
  created_at: string;
  updated_at: string;
  use_count: number;
}

export const createDescription = async (
  accountId: string,
  data: {
    title: string;
    prompt: string;
    creative_type: string;
  },
): Promise<CreateDescriptionResponse> => {
  const res = await axiosInstance.post<CreateDescriptionResponse>(
    `/creative/${accountId}/description/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface LabelDescriptionConfig {
  id: string;
  title: string;
  prompt: string;
  creative_type: string | null;
  created_at: string;
  updated_at: string;
  use_count: number;
}

interface GetLabelByIdResponse {
  id: string;
  title: string;
  prompt: string;
  predefined_labels: string[];
  creative_type: string | null;
  label_type: "unique" | "multiple";
  is_active: boolean;
  created_at: string;
  updated_at: string;
  description_configs: LabelDescriptionConfig[];
  metadata_configs: string[] | null;
}

export const getLabelById = async (
  accountId: string,
  labelId: string,
): Promise<GetLabelByIdResponse> => {
  const res = await axiosInstance.get<GetLabelByIdResponse>(
    `/creative/${accountId}/label/${labelId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface GetDescriptionByIdResponse {
  id: string;
  title: string;
  prompt: string;
  creative_type: string | null;
  created_at: string;
  updated_at: string;
  use_count: number;
}

export const getDescriptionById = async (
  accountId: string,
  descriptionId: string,
): Promise<GetDescriptionByIdResponse> => {
  const res = await axiosInstance.get<GetDescriptionByIdResponse>(
    `/creative/${accountId}/description/${descriptionId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface EditLabelRequest {
  title?: string;
  prompt?: string;
  description_configs?: string[];
  metadata_configs?: string[];
  creative_type?: string | null;
  label_type?: "unique" | "multiple";
}

interface EditLabelResponse {
  id: string;
  title: string;
  prompt: string;
  predefined_labels: string[];
  creative_type: string | null;
  label_type: "unique" | "multiple";
  is_active: boolean;
  created_at: string;
  updated_at: string;
  description_configs: {
    id: string;
    title: string;
    prompt: string;
    creative_type: string | null;
    created_at: string;
    updated_at: string;
    use_count: number;
  }[];
  metadata_configs: string[] | null;
}

export const editLabel = async (
  accountId: string,
  labelId: string,
  data: EditLabelRequest,
): Promise<EditLabelResponse> => {
  const res = await axiosInstance.put<EditLabelResponse>(
    `/creative/${accountId}/label/${labelId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface EditDescriptionRequest {
  title?: string;
  prompt?: string;
}

interface EditDescriptionResponse {
  id: string;
  title: string;
  prompt: string;
  creative_type: string | null;
  created_at: string;
  updated_at: string;
  use_count: number;
}

export const editDescription = async (
  accountId: string,
  descriptionId: string,
  data: EditDescriptionRequest,
): Promise<EditDescriptionResponse> => {
  const res = await axiosInstance.put<EditDescriptionResponse>(
    `/creative/${accountId}/description/${descriptionId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface DeleteResponse {
  status: string;
}

export const deleteLabel = async (
  accountId: string,
  labelId: string,
): Promise<DeleteResponse> => {
  const res = await axiosInstance.delete<DeleteResponse>(
    `/creative/${accountId}/label/${labelId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteDescription = async (
  accountId: string,
  descriptionId: string,
): Promise<DeleteResponse> => {
  const res = await axiosInstance.delete<DeleteResponse>(
    `/creative/${accountId}/description/${descriptionId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// ── V2 Label Config APIs ──────────────────────────────────────────────

export const getLabelV2ById = async (
  accountId: string,
  labelConfigId: string,
): Promise<CreateLabelV2Response> => {
  const res = await axiosInstance.get<CreateLabelV2Response>(
    `/creative/${accountId}/label-v2/${labelConfigId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

interface EditLabelV2Request {
  title?: string;
  prompt?: string;
  creative_type?: string | null;
  label_type?: "unique" | "multiple";
  label_sources?: {
    label_source: "auto" | "creative" | "ad_metadata";
    overview_selection_type?: "auto" | "manual";
    metadata_selection_type?: "auto" | "manual";
    overview_ids: string[];
    metadata: string[];
  };
  remember_label_groups?: boolean;
  label_groups?: {
    rename?: Array<{ id: string; name: string }>;
    delete?: string[];
    add?: Array<{ name: string; description?: string }>;
  };
}

export const editLabelV2 = async (
  accountId: string,
  labelConfigId: string,
  data: EditLabelV2Request,
): Promise<CreateLabelV2Response> => {
  const res = await axiosInstance.put<CreateLabelV2Response>(
    `/creative/${accountId}/label-v2/${labelConfigId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteLabelV2 = async (
  accountId: string,
  labelConfigId: string,
): Promise<DeleteResponse> => {
  const res = await axiosInstance.delete<DeleteResponse>(
    `/creative/${accountId}/label-v2/${labelConfigId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// ── Label Group Creatives ──────────────────────────────────────────────

export interface LabelGroupCreativeItem extends Creative {
  creative_id: string;
  ad_id?: string;
  ad_name: string;
  type: "image" | "video" | "mixed";
  platform?: Platform;
}

interface LabelGroupCreativesResponse {
  label_group: {
    id: string;
    name: string;
    description: string;
    label_config_id: string;
  };
  total_creatives: number;
  creatives: LabelGroupCreativeItem[];
  page?: number;
  page_size?: number;
  total_pages?: number;
}

export const fetchLabelGroupCreatives = async (
  accountId: string,
  labelGroupId: string,
  page?: number,
  pageSize?: number,
): Promise<LabelGroupCreativesResponse> => {
  const params: { page?: number; page_size?: number } = {};
  if (page != null) params.page = page;
  if (pageSize != null) params.page_size = pageSize;
  const res = await axiosInstance.get<LabelGroupCreativesResponse>(
    `/creative/${accountId}/label-v2/group/${labelGroupId}/creatives/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: Object.keys(params).length ? params : undefined,
    },
  );
  return res.data;
};
